import React, { useCallback, useState } from 'react'
import { Box, Flex, Text } from '@fundamentals'
import { useUpdateNotification } from '@core/react-query/features/notifications/hooks'
import { useGetProfile } from '@core/react-query/features/profile'
import {
  AppNotification,
  AppNotificationReferenceObject,
} from '@core/entities/notifications'
import {
  CheckCircleOutlineOutlined,
  MarkEmailUnreadOutlined,
} from '@mui/icons-material'
import { useRouter } from 'next/router'
import { PageUrls } from '@core/page-urls'
import { Divider, IconButton } from '@common/material'
import { useDrawer } from '@common/GlobalDrawer'
import { useAnalytics } from '@common/hooks'

type Props = {
  notification: AppNotification
  readOnly?: boolean
  onItemClick?: () => void
}

const TOUCHABLE_REFERENCE_OBJECTS: AppNotificationReferenceObject[] = [
  'PERMIT',
  'CHECKLIST',
  'TASK',
  'COMPANY_ONBOARDING',
  'COMPANY_COMPLIANCE',
  'COMPETENCY',
  'OBSERVATION',
  'REGISTER_ITEM',
]

const NotificationsListItem: React.FC<Props> = ({
  notification,
  readOnly = false,
  onItemClick,
}) => {
  const router = useRouter()
  const { hideDrawer } = useDrawer()
  const { sendAnalyticsEvent } = useAnalytics()
  const { data: profile } = useGetProfile()
  const { mutate: updateNotification } = useUpdateNotification()
  const [showItemAction, setShowItemAction] = useState(false)

  const { isRead, referenceObjectType, action } = notification

  const isNotificationTouchable =
    TOUCHABLE_REFERENCE_OBJECTS.includes(referenceObjectType)

  const description = notification.getDescription({
    organisations: profile?.organisations ?? [],
  })

  const trackNotificationInteraction = useCallback(() => {
    sendAnalyticsEvent({
      eventName: 'notification.interaction',
      eventProperties: {
        platform: 'web',
        notificationType: `${referenceObjectType} - ${action}`,
        alreadyRead: isRead,
      },
    })
  }, [sendAnalyticsEvent, referenceObjectType, action, isRead])

  const onNotificationClick = useCallback(() => {
    if (!readOnly) {
      // Track the interaction
      trackNotificationInteraction()

      // Update notification read status if it wasn't read
      if (!isRead) {
        updateNotification({
          userId: profile.id,
          notificationId: notification.id,
          read: true,
        })
      }

      if (notification.isTargetResultOfType('PERMIT')) {
        // The project stub is optional on the payload, so there is nowhere to navigate without it
        const projectId = notification.targetResult.project?.id
        if (projectId) {
          router.push(
            PageUrls.projectPermitDetail({
              organisationId: notification.targetResult.organisationId,
              projectId,
              permitId: notification.targetResult.id,
            }),
          )
        }
      } else if (notification.isTargetResultOfType('CHECKLIST')) {
        const organisationId = notification.targetResult.organisationId
        // The project stub is optional on the payload, so there is nowhere to navigate without it
        const projectId = notification.targetResult.project?.id
        const checklistId = notification.targetResult.id
        if (projectId) {
          const inspectionUrl = PageUrls.projectInspection({
            organisationId,
            projectId,
            checklistId,
          })
          const checklistUrl = PageUrls.projectChecklistDetail({
            organisationId,
            projectId,
            checklistId,
          })
          router.push(
            notification.targetResult.isInspection
              ? inspectionUrl
              : checklistUrl,
          )
        }
      } else if (notification.isTargetResultOfType('TASK')) {
        router.push(
          PageUrls.task(
            notification.targetResult.organisationId,
            notification.targetResult.id,
          ),
        )
      } else if (
        notification.isTargetResultOfType('COMPANY_ONBOARDING') ||
        notification.isTargetResultOfType('COMPANY_COMPLIANCE')
      ) {
        router.push(
          PageUrls.organisationCompanyDetail({
            organisationId: notification.targetResult.organisationId,
            companyId: notification.targetResult.companyId,
          }),
        )
      } else if (notification.isTargetResultOfType('COMPETENCY')) {
        router.push({
          pathname: PageUrls.organisationUserProfile({
            organisationId: notification.targetResult.organisationId,
            userId: notification.targetResult.userId,
          }),
          query: {
            tab: 'competencies',
          },
        })
      } else if (notification.isTargetResultOfType('OBSERVATION')) {
        router.push(
          PageUrls.projectObservation({
            organisationId: notification.targetResult.organisationId,
            projectId: notification.targetResult.projectId,
            observationId: notification.targetResult.id,
            status: notification.targetResult.status,
          }),
        )
      } else if (notification.isTargetResultOfType('REGISTER_ITEM')) {
        if (
          notification.targetResult.organisationId &&
          notification.targetResult.registerId
        ) {
          router.push(
            PageUrls.registerItemDetails({
              organisationId: notification.targetResult.organisationId,
              registerId: notification.targetResult.registerId,
              registerItemId: notification.targetResult.id,
            }),
          )
        }
      }

      hideDrawer()
      onItemClick?.()
    }
  }, [isRead, onItemClick])

  const onToggleReadStatus = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      // Track the interaction
      trackNotificationInteraction()

      updateNotification({
        userId: profile.id,
        notificationId: notification.id,
        read: !isRead,
      })
    },
    [
      isRead,
      trackNotificationInteraction,
      updateNotification,
      profile?.id,
      notification.id,
    ],
  )

  return (
    <>
      <Box
        px={3}
        py={2}
        onClick={!readOnly ? onNotificationClick : undefined}
        onMouseEnter={() => setShowItemAction(true)}
        onMouseLeave={() => setShowItemAction(false)}
        sx={{
          cursor: isNotificationTouchable ? 'pointer' : 'default',
          '&:hover': isNotificationTouchable && { bgcolor: 'grey.100' },
        }}
        bgcolor='white'
      >
        <Flex alignItems='center' width='100%' position='relative'>
          {!isRead && (
            <Box
              height={8}
              width={8}
              borderRadius={8}
              bgcolor='primary.main'
              mr={1}
            />
          )}

          <Text pr={2} fontSize={13} fontWeight={isRead ? 'normal' : 'medium'}>
            {notification.getTitle()}
          </Text>
          {!readOnly && showItemAction && (
            <Box
              position='absolute'
              right={-8}
              top='50%'
              sx={{ transform: 'translateY(-50%)' }}
            >
              <IconButton
                onClick={onToggleReadStatus}
                size='small'
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '4px',
                  color: 'grey.500',
                  '&:hover': { bgcolor: 'transparent', color: 'grey.600' },
                }}
              >
                {isRead ? (
                  <MarkEmailUnreadOutlined sx={{ width: 16, height: 16 }} />
                ) : (
                  <CheckCircleOutlineOutlined sx={{ width: 16, height: 16 }} />
                )}
              </IconButton>
            </Box>
          )}
        </Flex>
        {!!description && (
          <Text
            fontSize={13}
            color='text.secondary'
            sx={{ whiteSpace: 'pre-wrap' }}
          >
            {description}
          </Text>
        )}
        <Text fontSize={12} color='text.disabled'>
          {notification.getFormattedCreatedOn()}
        </Text>
      </Box>
      <Divider sx={{ borderBottomWidth: '0.5px' }} />
    </>
  )
}

const MemoizedNotificationsListItem = React.memo(
  NotificationsListItem,
  (prevProps, nextProps) =>
    // Only re-render if the id or read status changes
    // Those are the only properties that can change
    prevProps.notification.id === nextProps.notification.id &&
    prevProps.notification.isRead === nextProps.notification.isRead,
)

export { MemoizedNotificationsListItem as NotificationsListItem }
